import type { AnchoredExchange } from '../exchanges.js'

/**
 * The section types with a colour token, as the section block maps them (plugins/section/src/block.module.css:
 * `section--{type}` → `--tuna-section-{type}`). Any other type is "other", like the block's fallback.
 */
const SECTION_COLOURS = {
  song: 'var(--tuna-section-song)',
  intro: 'var(--tuna-section-intro)',
  verse: 'var(--tuna-section-verse)',
  chorus: 'var(--tuna-section-chorus)',
  outro: 'var(--tuna-section-outro)',
  bridge: 'var(--tuna-section-bridge)',
  middle8: 'var(--tuna-section-middle8)',
  riff: 'var(--tuna-section-riff)',
  other: 'var(--tuna-section-other)',
} as const
type SectionColourKey = keyof typeof SECTION_COLOURS

function isSectionColourKey(value: string): value is SectionColourKey {
  return value in SECTION_COLOURS
}

const colourOfType = (sectionType: unknown) => {
  const key = typeof sectionType === 'string' ? sectionType.toLowerCase() : ''
  return SECTION_COLOURS[isSectionColourKey(key) ? key : 'other']
}

/** Element id → its section's colour, per document version (Slate replaces the children array on every change) */
const cache = new WeakMap<readonly unknown[], ReadonlyMap<string, string>>()

const colourMap = (children: readonly unknown[]): ReadonlyMap<string, string> => {
  const cached = cache.get(children)
  if (cached) {
    return cached
  }
  const colours = new Map<string, string>()
  const visit = (nodes: unknown, colour: string | undefined) => {
    if (!Array.isArray(nodes)) {
      return
    }
    for (const node of nodes) {
      if (node === null || typeof node !== 'object' || typeof (node as { type?: unknown }).type !== 'string') {
        continue
      }
      const {
        id,
        type,
        sectionType,
        children: nested,
      } = node as {
        id?: unknown
        type: string
        sectionType?: unknown
        children?: unknown
      }
      const own = type === 'section' ? colourOfType(sectionType) : colour
      if (own && typeof id === 'string' && id.length > 0) {
        colours.set(id, own)
      }
      visit(nested, own)
    }
  }
  visit(children, undefined)
  cache.set(children, colours)
  return colours
}

/**
 * The colour of the section an exchange's element sits in, or its container once the element is gone; `undefined`
 * outside any section or the song (the chip stays neutral). The chat makeover's anchor chip (#2729).
 */
export const sectionColourOf = (
  children: readonly unknown[],
  anchor: AnchoredExchange['anchor']
): string | undefined => {
  if (!anchor) {
    return undefined
  }
  const colours = colourMap(children)
  return colours.get(anchor.elementId) ?? (anchor.containerId ? colours.get(anchor.containerId) : undefined)
}
